// Product Highlights Layout Fix - Complete Version with Stability Improvements
if (!customElements.get('product-info')) {
  customElements.define('product-info', class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.quantity-input');
      this.currentVariant = this.querySelector('.product-variant-id');
      this.variantSelects = this.querySelector('variant-radios');
      this.submitButton = this.querySelector('[type="submit"]');
      
      // Stability improvements
      this.isAdjusting = false;
      this.lastAdjustmentTime = 0;
      this.adjustmentDebounceTimer = null;
      this.resizeDebounceTimer = null;
      this.lastKnownHeight = 0;
      this.stabilityCheckCount = 0;
    }

    cartUpdateUnsubscriber = undefined;
    variantChangeUnsubscriber = undefined;
    resizeObserver = undefined;
    stabilityMonitorId = undefined;
    performanceLogCount = 0;
    lastPerformanceCheck = undefined;

    connectedCallback() {
      if (!this.input) return;
      this.quantityForm = this.querySelector('.product-form-quantity');
      if (!this.quantityForm) return;
      this.setQuantityBoundries();
      
      if (!this.dataset.originalSection) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
      }
      
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
        if (event.data.sectionId !== sectionId) return;
        this.updateQuantityRules(event.data.sectionId, event.data.html);
        this.setQuantityBoundries();
        // Adjust highlights after variant change with debouncing
        this.debouncedAdjustHeight();
      });
      
      // Initialize product highlights with height matching
      this.initializeProductHighlights();
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      if (this.stabilityMonitorId) {
        clearInterval(this.stabilityMonitorId);
      }
      if (this.adjustmentDebounceTimer) {
        clearTimeout(this.adjustmentDebounceTimer);
      }
      if (this.resizeDebounceTimer) {
        clearTimeout(this.resizeDebounceTimer);
      }
      // Clean up mobile highlights observers and handlers
      if (this.mobileResizeHandler) {
        window.removeEventListener('resize', this.mobileResizeHandler);
      }
      if (this.mobileImageObserver) {
        this.mobileImageObserver.disconnect();
      }
      if (this.galleryObserver) {
        this.galleryObserver.disconnect();
      }
    }

    initializeProductHighlights() {
      // Initial adjustment
      this.adjustHighlightsHeight();
      
      // Handle window resize with debouncing
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.adjustHighlightsHeight(), 250);
      });
      
      // Use ResizeObserver for media container changes
      const mediaContainer = document.querySelector('.media-gallery-main');
      if (mediaContainer && window.ResizeObserver) {
        let previousHeight = 0;
        this.resizeObserver = new ResizeObserver(entries => {
          for (let entry of entries) {
            const currentHeight = Math.round(entry.contentRect.height);
            // Only adjust if change is significant (>5px)
            if (Math.abs(currentHeight - previousHeight) > 5) {
              previousHeight = currentHeight;
              this.debouncedAdjustHeight();
            }
          }
        });
        this.resizeObserver.observe(mediaContainer);
      }
      
      // Adjust after images load
      window.addEventListener('load', () => this.adjustHighlightsHeight());
      
      // Multiple timing adjustments for dynamic content - reduced frequency
      setTimeout(() => this.adjustHighlightsHeight(), 100);
      setTimeout(() => this.adjustHighlightsHeight(), 500);
      
      // Setup accessibility
      this.setupHighlightAccessibility();
      
      // Add REDUCED stability check (only runs 10 times then stops)
      this.addReducedStabilityCheck();
      
      // Ensure proper column containment
      this.enforceColumnBoundaries();
      
      // Enhanced MutationObserver for carousel and image changes
      const observer = new MutationObserver((mutations) => {
        let shouldAdjust = false;
        
        mutations.forEach((mutation) => {
          // Check for active class changes on carousel items
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target;
            
            // Check if this is a carousel slide becoming active
            if (target.classList.contains('product-media-item')) {
              if (target.classList.contains('is-active')) {
                shouldAdjust = true;
                console.log('Carousel slide changed - recalculating highlights');
              }
            }
          }
          
          // Check for any image src changes
          if (mutation.type === 'attributes' && mutation.attributeName === 'src') {
            shouldAdjust = true;
          }
        });
        
        if (shouldAdjust && window.innerWidth < 1024 ) {
          // Delay to ensure new image is fully rendered
          setTimeout(() => {
            const productInfo = document.querySelector('product-info');
            if (productInfo && productInfo.adjustMobileHighlights) {
              productInfo.adjustMobileHighlights();
            }
          }, 100);
        }
      });

      // Observe carousel for active slide changes
      const carousel = document.querySelector('#Slider-Gallery-' + this.dataset.section);
      if (carousel) {
        observer.observe(carousel, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ['class', 'src']
        });
      }
      
      // Observe both gallery and image elements
      const galleryElement = document.querySelector('.media-gallery-main');
      if (galleryElement) {
        observer.observe(galleryElement, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ['class', 'style', 'src']
        });
      }
      
      // Listen for carousel navigation button clicks
      const sliderButtons = document.querySelectorAll('.slider-button');
      sliderButtons.forEach(button => {
        button.addEventListener('click', () => {
          // Wait for slide transition to complete
          setTimeout(() => {
            if (window.innerWidth < 1024 ) {
              this.adjustMobileHighlights();
            }
          }, 300);
        });
      });

      // Listen for thumbnail clicks (if using thumbnail navigation)
      const thumbnails = document.querySelectorAll('.thumbnail');
      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          setTimeout(() => {
            if (window.innerWidth < 1024 ) {
              this.adjustMobileHighlights();
            }
          }, 300);
        });
      });

      // Listen for pagination dot clicks
      const paginationDots = document.querySelectorAll('.pagination-dot');
      paginationDots.forEach(dot => {
        dot.addEventListener('click', () => {
          setTimeout(() => {
            if (window.innerWidth < 1024 ) {
              this.adjustMobileHighlights();
            }
          }, 300);
        });
      });
      
      // Handle touch/swipe events for mobile carousel
      if (window.innerWidth < 1024 ) {
        const gallery = document.querySelector('.product__media-list');
        if (gallery) {
          let touchStartX = 0;
          
          gallery.addEventListener('touchstart', (e) => {
            touchStartX = e.touches[0].clientX;
          });
          
          gallery.addEventListener('touchend', (e) => {
            const touchEndX = e.changedTouches[0].clientX;
            const difference = Math.abs(touchStartX - touchEndX);
            
            // If swipe detected, recalculate after transition
            if (difference > 50) {
              setTimeout(() => {
                this.adjustMobileHighlights();
              }, 400);
            }
          });
        }
      }
    }

    debouncedAdjustHeight() {
      if (this.adjustmentDebounceTimer) {
        clearTimeout(this.adjustmentDebounceTimer);
      }
      this.adjustmentDebounceTimer = setTimeout(() => {
        this.adjustHighlightsHeight();
      }, 150);
    }

    adjustHighlightsHeight() {
      // Prevent concurrent adjustments
      if (this.isAdjusting) return;
      
      // Throttle adjustments
      const now = Date.now();
      if (now - this.lastAdjustmentTime < 100) return;
      
      this.isAdjusting = true;
      this.lastAdjustmentTime = now;
      
    const screenWidth = window.innerWidth;
      const isDesktop = screenWidth >= 1024;
      const isTablet = screenWidth >= 768 && screenWidth < 1024;
      const isMobile = screenWidth < 768;

      // Use appropriate layout for each breakpoint
      if (isDesktop) {
        this.adjustDesktopHighlights(screenWidth);
      } else if (isTablet) {
        this.adjustTabletHighlights();
      } else {
        this.adjustMobileHighlights();
      }
      
      // Reset adjustment flag
      requestAnimationFrame(() => {
        this.isAdjusting = false;
      });
    }

    adjustDesktopHighlights(screenWidth = window.innerWidth) {
      const mediaContainer = document.querySelector('.media-gallery-main');
      const highlightsDesktop = document.querySelector('.product-highlights-desktop');
      const wrapper = document.querySelector('.media-and-highlights-wrapper');
      
      if (!mediaContainer || !highlightsDesktop || !wrapper) return;
      
      // Check if height hasn't changed significantly
      const currentImageHeight = this.getCurrentImageHeight();
      if (this.lastKnownHeight > 0 && Math.abs(currentImageHeight - this.lastKnownHeight) < 5) {
        return; // Skip adjustment if change is insignificant
      }
      this.lastKnownHeight = currentImageHeight;
      
      // Ensure highlights are visible
      highlightsDesktop.style.display = 'flex';
      highlightsDesktop.style.opacity = '1';
      highlightsDesktop.style.visibility = 'visible';
      
      // Get the actual height of the product image
      const activeMedia = mediaContainer.querySelector('.product-media-item.is-active');
      if (activeMedia) {
        const mediaElement = activeMedia.querySelector('.product__media.media') || activeMedia;
        const img = mediaElement.querySelector('img');
        
        // Function to set dimensions with perfect alignment
        const setDimensions = () => {
          const imgRect = img ? img.getBoundingClientRect() : mediaElement.getBoundingClientRect();
          const mediaHeight = Math.round(imgRect.height);
          
          if (mediaHeight > 0) {
            const highlightsContainer = highlightsDesktop.querySelector('.product-highlights');
            if (highlightsContainer) {
              const iconCount = highlightsContainer.children.length;
              
              // Base dimensions
              const verticalGap = 25; // 25px between icons
              const horizontalGap = 25; // 25px from image
              const baseViewport = 768;
              
              // Calculate available space for each icon wrapper
              const totalGapHeight = verticalGap * (iconCount - 1);
              const availableHeightForIcons = mediaHeight - totalGapHeight;
              const iconWrapperSize = Math.floor(availableHeightForIcons / iconCount);
              
              // Ensure minimum size
              const finalIconSize = Math.max(100, Math.min(200, iconWrapperSize));
              const finalGapSize = iconCount > 1 ? 
                Math.floor((mediaHeight - (finalIconSize * iconCount)) / (iconCount - 1)) : 0;
              
              // Set CSS variables
              wrapper.style.setProperty('--image-height', `${mediaHeight}px`);
              wrapper.style.setProperty('--icon-size', `${finalIconSize}px`);
              wrapper.style.setProperty('--icon-content-size', `${Math.round(finalIconSize * 0.4)}px`);
              wrapper.style.setProperty('--gap-size', `${finalGapSize}px`);
              wrapper.style.setProperty('--horizontal-gap', `${horizontalGap}px`);
              
              // Apply to container
              highlightsDesktop.style.height = `${mediaHeight}px`;
              highlightsDesktop.style.minHeight = `${mediaHeight}px`;
              highlightsDesktop.style.maxHeight = `${mediaHeight}px`;
              
              // Set highlights container
              highlightsContainer.style.height = `${mediaHeight}px`;
              highlightsContainer.style.display = 'flex';
              highlightsContainer.style.flexDirection = 'column';
              highlightsContainer.style.justifyContent = 'space-between';
              highlightsContainer.style.gap = `${finalGapSize}px`;
              highlightsContainer.style.padding = '0';
              
              // Update icon wrappers
              const iconWrappers = highlightsContainer.querySelectorAll('.highlight-icon-wrapper');
              iconWrappers.forEach(wrapper => {
                wrapper.style.width = `${finalIconSize}px`;
                wrapper.style.height = `${finalIconSize}px`;
                wrapper.style.minWidth = `${finalIconSize}px`;
                wrapper.style.minHeight = `${finalIconSize}px`;
                wrapper.style.maxWidth = `${finalIconSize}px`;
                wrapper.style.maxHeight = `${finalIconSize}px`;
                wrapper.style.borderRadius = `${Math.round(finalIconSize * 0.16)}px`;
              });
              
              wrapper.style.gap = `${horizontalGap}px`;
            }
          }
        };
        
        // Set dimensions immediately and after image loads
        setDimensions();
        
        if (img && !img.complete) {
          img.addEventListener('load', setDimensions, { once: true });
        }
      }
      
      // Hide mobile highlights
      const highlightsMobile = document.querySelector('.product-highlights-mobile');
      if (highlightsMobile) {
        highlightsMobile.style.display = 'none';
      }
    }

    adjustMobileHighlights() {
      const highlightsMobile = document.querySelector('.product-highlights-mobile');
      const highlightsDesktop = document.querySelector('.product-highlights-desktop');
      
      if (highlightsMobile) {
        // Show mobile highlights
        highlightsMobile.style.display = 'block';
        highlightsMobile.style.opacity = '1';
        highlightsMobile.style.visibility = 'visible';
        
        // Get the ACTIVE product image to match its width
        const activeSlide = document.querySelector('.product-media-item.is-active');
        let productImage = null;
        let mediaContainer = null;

        if (activeSlide) {
          productImage = activeSlide.querySelector('.product__media.media img');
          mediaContainer = activeSlide.querySelector('.product__media.media');
        } else {
          // Fallback to first visible image
          productImage = document.querySelector('.product__media.media img');
          mediaContainer = document.querySelector('.product__media.media');
        }

        // Force layout recalculation
        if (mediaContainer) {
          mediaContainer.offsetHeight; // Force reflow
        }
        
        const highlights = highlightsMobile.querySelector('.product-highlights');
        
        if (highlights && (productImage || mediaContainer)) {
          // Get the actual rendered width of the product image
          const imageElement = productImage || mediaContainer;
          const imageRect = imageElement.getBoundingClientRect();
          const imageWidth = Math.round(imageRect.width);
          
          // Set the highlights container to match image width exactly
          highlights.style.width = `${imageWidth}px`;
          highlights.style.maxWidth = `${imageWidth}px`;
          highlights.style.margin = '0 auto';
          highlights.style.display = 'flex';
          highlights.style.justifyContent = 'space-between';
          highlights.style.alignItems = 'flex-start';
          highlights.style.gap = '15px';
          
          const items = highlights.querySelectorAll('.product-highlight-item');
          const itemCount = items.length;
          
          if (itemCount > 0 && imageWidth > 0) {
        // Calculate icon size based on actual image width
            const gap = 15; // Fixed 15px gap
            const totalGaps = gap * (itemCount - 1);
            const availableWidth = imageWidth - totalGaps;
            
            // Dynamic max size based on screen width
            let maxIconSize = 115; // Default for small screens
            if (window.innerWidth >= 480 && window.innerWidth < 1024) {
              maxIconSize = 175; // Allow larger icons on medium screens
            }
            
            const iconSize = Math.min(maxIconSize, Math.max(60, Math.floor(availableWidth / itemCount)));
            
            // Apply to each item with text INSIDE wrapper
            items.forEach(item => {
              item.style.width = `${iconSize}px`;
              item.style.flex = '0 0 auto';
              
              const iconWrapper = item.querySelector('.highlight-icon-wrapper');
              const text = item.querySelector('.highlight-text');
              
              if (iconWrapper) {
                // Style wrapper to contain both icon and text
                iconWrapper.style.width = `${iconSize}px`;
                iconWrapper.style.height = `${iconSize}px`;
                iconWrapper.style.minWidth = `${iconSize}px`;
                iconWrapper.style.minHeight = `${iconSize}px`;
                iconWrapper.style.borderRadius = `${Math.round(iconSize * 0.16)}px`;
                iconWrapper.style.display = 'flex';
                iconWrapper.style.flexDirection = 'column';
                iconWrapper.style.alignItems = 'center';
                iconWrapper.style.justifyContent = 'center';
                iconWrapper.style.position = 'relative';
                iconWrapper.style.padding = '8px';
                iconWrapper.style.boxSizing = 'border-box';
                
                // Style the icon inside
                const icon = iconWrapper.querySelector('.highlight-icon, .highlight-icon-svg svg, .highlight-icon-placeholder svg, img');
                if (icon) {
                  const iconContentSize = Math.round(iconSize * 0.35);
                  icon.style.width = `${iconContentSize}px`;
                  icon.style.height = `${iconContentSize}px`;
                  icon.style.flexShrink = '0';
                }
                
                // Move text inside wrapper if it's outside
                if (text && text.parentElement !== iconWrapper) {
                  iconWrapper.appendChild(text);
                }
                
                // Style text inside wrapper
                if (text) {
                  const fontSize = Math.max(9, Math.round(iconSize * 0.11));
                  text.style.fontSize = `${fontSize}px`;
                  text.style.marginTop = '6px';
                  text.style.lineHeight = '1.1';
                  text.style.textAlign = 'center';
                  text.style.fontWeight = '600';
                  text.style.position = 'relative';
                  text.style.whiteSpace = 'nowrap';
                  text.style.overflow = 'hidden';
                  text.style.textOverflow = 'ellipsis';
                  text.style.maxWidth = '100%';
                  text.style.color = 'var(--hl-color, #374151)';
                }
              }
            });
            
            console.log('Mobile Highlights Aligned to Image:', {
              imageWidth,
              iconSize,
              gap,
              itemCount
            });
          }
        }
        
        // Center the mobile highlights container relative to viewport
        highlightsMobile.style.display = 'flex';
        highlightsMobile.style.justifyContent = 'center';
        highlightsMobile.style.width = '100%';
        highlightsMobile.style.padding = '0';
      }
      
      // Hide desktop highlights
      if (highlightsDesktop) {
        highlightsDesktop.style.display = 'none';
      }
    }

adjustTabletHighlights() {
  const highlightsMobile = document.querySelector('.product-highlights-mobile');
  const highlightsDesktop = document.querySelector('.product-highlights-desktop');
  
  // Use mobile highlights container for tablet horizontal layout
  if (highlightsMobile) {
    highlightsMobile.style.display = 'block';
    highlightsMobile.style.opacity = '1';
    highlightsMobile.style.visibility = 'visible';
    
    // Position in left column under image
    const mediaGallery = document.querySelector('.media-gallery-main');
    if (mediaGallery) {
      // Ensure highlights are positioned after gallery in DOM flow
      const galleryParent = mediaGallery.parentElement;
      if (!highlightsMobile.parentElement.classList.contains('media-gallery-container')) {
        // Move highlights to be child of media container if not already
        const mediaContainer = document.querySelector('.media-gallery-container') || galleryParent;
        if (mediaContainer && !mediaContainer.contains(highlightsMobile)) {
          mediaContainer.appendChild(highlightsMobile);
        }
      }
    }
    
    // Get the active product image
    const activeSlide = document.querySelector('.product-media-item.is-active');
    let productImage = null;
    let mediaContainer = null;

    if (activeSlide) {
      productImage = activeSlide.querySelector('.product__media.media img');
      mediaContainer = activeSlide.querySelector('.product__media.media');
    } else {
      productImage = document.querySelector('.product__media.media img');
      mediaContainer = document.querySelector('.product__media.media');
    }
    
    const highlights = highlightsMobile.querySelector('.product-highlights');
    
    if (highlights && (productImage || mediaContainer)) {
      const imageElement = productImage || mediaContainer;
      const imageRect = imageElement.getBoundingClientRect();
      const imageWidth = Math.round(imageRect.width);
      
      // Set highlights to match image width
      highlights.style.width = `${imageWidth}px`;
      highlights.style.maxWidth = `${imageWidth}px`;
      highlights.style.margin = '15px auto 0';
      highlights.style.display = 'flex';
      highlights.style.justifyContent = 'space-between';
      highlights.style.alignItems = 'flex-start';
      highlights.style.gap = '20px';
      
      const items = highlights.querySelectorAll('.product-highlight-item');
      const itemCount = items.length;
      
      if (itemCount > 0 && imageWidth > 0) {
        // Calculate icon size for tablet
        const gap = 20;
        const totalGaps = gap * (itemCount - 1);
        const availableWidth = imageWidth - totalGaps;
        const iconSize = Math.min(120, Math.max(80, Math.floor(availableWidth / itemCount)));
        
        items.forEach(item => {
          item.style.width = `${iconSize}px`;
          item.style.flex = '0 0 auto';
          
          const iconWrapper = item.querySelector('.highlight-icon-wrapper');
          const text = item.querySelector('.highlight-text');
          
          if (iconWrapper) {
            // Style wrapper for tablet
            iconWrapper.style.width = `${iconSize}px`;
            iconWrapper.style.height = `${iconSize}px`;
            iconWrapper.style.minWidth = `${iconSize}px`;
            iconWrapper.style.minHeight = `${iconSize}px`;
            iconWrapper.style.borderRadius = `${Math.round(iconSize * 0.12)}px`;
            iconWrapper.style.display = 'flex';
            iconWrapper.style.flexDirection = 'column';
            iconWrapper.style.alignItems = 'center';
            iconWrapper.style.justifyContent = 'center';
            iconWrapper.style.padding = '10px';
            iconWrapper.style.boxSizing = 'border-box';
            
            // Style icon
            const icon = iconWrapper.querySelector('.highlight-icon, .highlight-icon-svg svg, .highlight-icon-placeholder svg, img');
            if (icon) {
              const iconContentSize = Math.round(iconSize * 0.4);
              icon.style.width = `${iconContentSize}px`;
              icon.style.height = `${iconContentSize}px`;
              icon.style.flexShrink = '0';
            }
            
            // Move text inside wrapper if needed
            if (text && text.parentElement !== iconWrapper) {
              iconWrapper.appendChild(text);
            }
            
            // Style text for tablet
            if (text) {
              text.style.fontSize = '11px';
              text.style.marginTop = '8px';
              text.style.lineHeight = '1.2';
              text.style.textAlign = 'center';
              text.style.fontWeight = '600';
              text.style.whiteSpace = 'nowrap';
              text.style.overflow = 'hidden';
              text.style.textOverflow = 'ellipsis';
              text.style.maxWidth = '100%';
              text.style.color = 'var(--hl-color, #374151)';
            }
          }
        });
      }
    }
    
    // Ensure proper positioning
    highlightsMobile.style.position = 'relative';
    highlightsMobile.style.marginTop = '15px';
    highlightsMobile.style.marginBottom = '15px';
    highlightsMobile.style.width = '100%';
  }
  
  // Hide desktop highlights
  if (highlightsDesktop) {
    highlightsDesktop.style.display = 'none';
  }
  
  // Hide mobile pagination dots on tablet
  const paginationContainer = document.querySelector('.mobile-pagination-container');
  if (paginationContainer) {
    paginationContainer.style.display = 'none';
  }
  
  // Ensure thumbnails are in correct position in DOM
  const thumbnailSlider = document.querySelector('.thumbnail-slider');
  const mediaContainerForThumbs = document.querySelector('.media-gallery-container');
  
  if (thumbnailSlider && mediaContainerForThumbs) {
    // Move thumbnails to be last child of media container
    if (!mediaContainerForThumbs.contains(thumbnailSlider)) {
      mediaContainerForThumbs.appendChild(thumbnailSlider);
    }
    
    // Ensure proper order
    thumbnailSlider.style.order = '3';
    
    // Make sure it's not hidden by any class
    thumbnailSlider.classList.remove('small-hide');
    thumbnailSlider.classList.add('tablet-show');
    
    // Force display with important flag
    thumbnailSlider.style.setProperty('display', 'flex', 'important');
    thumbnailSlider.style.setProperty('visibility', 'visible', 'important');
    thumbnailSlider.style.setProperty('opacity', '1', 'important');
    
    // Show navigation arrows
    const sliderButtons = thumbnailSlider.querySelectorAll('.slider-button');
    sliderButtons.forEach(button => {
      button.style.display = 'flex';
    });
  }
}

  

    styleIconWrapper(wrapper, size) {
      // Style the icon wrapper
      wrapper.style.width = `${size}px`;
      wrapper.style.height = `${size}px`;
      wrapper.style.minWidth = `${size}px`;
      wrapper.style.minHeight = `${size}px`;
      wrapper.style.borderRadius = `${Math.round(size * 0.16)}px`;
      
      // Style the icon inside
      const icon = wrapper.querySelector('.highlight-icon, .highlight-icon-svg svg, .highlight-icon-placeholder svg, img');
      if (icon) {
        const iconContentSize = Math.round(size * 0.5);
        icon.style.width = `${iconContentSize}px`;
        icon.style.height = `${iconContentSize}px`;
      }
      
      // Style the text
      const text = wrapper.parentElement.querySelector('.highlight-text');
      if (text) {
        const fontSize = Math.max(10, Math.round(size * 0.13));
        text.style.fontSize = `${fontSize}px`;
        text.style.marginTop = '8px';
        text.style.lineHeight = '1.2';
      }
    }

    enforceColumnBoundaries() {
      // Ensure highlights stay within left column
      const productGrid = document.querySelector('.product.grid');
      const mediaWrapper = document.querySelector('.product-media-wrapper');
      
      if (productGrid && mediaWrapper && window.innerWidth >= 990) {
        // Ensure proper grid structure
        productGrid.style.display = 'grid';
        productGrid.style.gridTemplateColumns = '60% 40%';
        productGrid.style.gap = '3rem';
        
        // Ensure media wrapper contains everything properly
        mediaWrapper.style.position = 'relative';
        mediaWrapper.style.overflow = 'visible';
        mediaWrapper.style.maxWidth = '100%';
      }
    }

    setupHighlightAccessibility() {
      const highlightWrappers = document.querySelectorAll('.highlight-icon-wrapper');
      
      highlightWrappers.forEach(wrapper => {
        // Ensure proper ARIA attributes
        if (!wrapper.hasAttribute('role')) {
          wrapper.setAttribute('role', 'button');
        }
        if (!wrapper.hasAttribute('tabindex')) {
          wrapper.setAttribute('tabindex', '0');
        }
        
        // Keyboard navigation
        wrapper.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            wrapper.click();
          }
        });
        
        // Add hover effect for desktop
        if (window.innerWidth >= 750) {
          wrapper.addEventListener('mouseenter', function() {
            this.style.transform = 'translateY(-3px) scale(1.05)';
          });
          
          wrapper.addEventListener('mouseleave', function() {
            this.style.transform = 'translateY(0) scale(1)';
          });
        }
        
        // Optional: Add click handler for additional functionality
        wrapper.addEventListener('click', function() {
          const text = this.parentElement.querySelector('.highlight-text');
          if (text && window.location.hostname === 'localhost') {
            console.log('Highlight clicked:', text.textContent);
          }
        });
      });
    }

    // Helper method to get current image height
    getCurrentImageHeight() {
      const mediaContainer = document.querySelector('.media-gallery-main');
      if (!mediaContainer) return 0;
      
      const activeItem = mediaContainer.querySelector('.product-media-item.is-active') || 
                        mediaContainer.querySelector('.product-media-item');
      if (!activeItem) return 0;
      
      const imageElement = activeItem.querySelector('.product__media.media.media--transparent') || 
                          activeItem.querySelector('.product__media.media') || 
                          activeItem;
      
      return imageElement ? Math.round(imageElement.getBoundingClientRect().height) : 0;
    }

    // Full recalculation method for persistent issues
    fullHeightRecalculation() {
      const highlightsDesktop = document.querySelector('.product-highlights-desktop');
      
      // Clear all stored references
      if (highlightsDesktop) {
        highlightsDesktop.removeAttribute('data-locked-height');
        highlightsDesktop.removeAttribute('data-image-ratio');
        highlightsDesktop.style.height = '';
        highlightsDesktop.style.minHeight = '';
        highlightsDesktop.style.maxHeight = '';
      }
      
      // Wait for layout to settle, then recalculate
      setTimeout(() => {
        this.adjustHighlightsHeight();
      }, 100);
    }

    addReducedStabilityCheck() {
      let checkCount = 0;
      const maxChecks = 10; // Only run 10 times then stop
      let lastImageHeight = 0;
      
      const stabilityMonitor = setInterval(() => {
        checkCount++;
        
        // Stop after max checks
        if (checkCount > maxChecks) {
          clearInterval(stabilityMonitor);
          this.stabilityMonitorId = null;
          return;
        }
        
        // Skip if not in desktop mode
        if (window.innerWidth < 750) return;
        
        const currentImageHeight = this.getCurrentImageHeight();
        
        // Only adjust if significant change (>10px)
        if (Math.abs(currentImageHeight - lastImageHeight) > 10) {
          lastImageHeight = currentImageHeight;
          this.debouncedAdjustHeight();
        }
      }, 2000);
      
      // Store the interval ID for cleanup
      this.stabilityMonitorId = stabilityMonitor;
    }

    // Existing methods (keep all original functionality)
    setQuantityBoundries() {
      const data = {
        cartQuantity: this.input.dataset.cartQuantity ? parseInt(this.input.dataset.cartQuantity) : 0,
        min: this.input.dataset.min ? parseInt(this.input.dataset.min) : 1,
        max: this.input.dataset.max ? parseInt(this.input.dataset.max) : null,
        step: this.input.step ? parseInt(this.input.step) : 1
      }

      let min = data.min;
      const max = data.max === null ? data.max : data.max - data.cartQuantity;
      if (max !== null) min = Math.min(min, max);
      if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

      this.input.min = min;
      this.input.max = max;
      this.input.value = min;
      publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
    }

    fetchQuantityRules() {
      if (!this.currentVariant || !this.currentVariant.value) return;
      this.querySelector('.quantity-rules-cart .loading-overlay').classList.remove('hidden');
      fetch(`${this.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.dataset.section}`).then((response) => {
        return response.text()
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateQuantityRules(this.dataset.section, html);
        this.setQuantityBoundries();
      })
      .catch(e => {
        console.error(e);
      })
      .finally(() => {
        this.querySelector('.quantity-rules-cart .loading-overlay').classList.add('hidden');
      });
    }

    updateQuantityRules(sectionId, html) {
      const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
      const selectors = ['.quantity-input', '.quantity-rules', '.quantity-label'];
      for (let selector of selectors) {
        const current = this.quantityForm.querySelector(selector);
        const updated = quantityFormUpdated.querySelector(selector);
        if (!current || !updated) continue;
        if (selector === '.quantity-input') {
          const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
          for (let attribute of attributes) {
            const valueUpdated = updated.getAttribute(attribute);
            if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated);
          }
        } else {
          current.innerHTML = updated.innerHTML;
        }
      }
    }
  });
}

// Global initialization for product highlights with LARGE icons
document.addEventListener('DOMContentLoaded', function() {
  function adjustAllHighlights() {
    const productInfo = document.querySelector('product-info');
    if (productInfo && productInfo.adjustHighlightsHeight) {
      productInfo.adjustHighlightsHeight();
    }
  }
  
  // Initial adjustment
  adjustAllHighlights();
  
  // Wait for all content to settle
  setTimeout(adjustAllHighlights, 300);
  setTimeout(adjustAllHighlights, 1000);
  
  // Handle window resize with debouncing
  let resizeTimer;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(adjustAllHighlights, 250);
  });
  
  // Enhanced MutationObserver for carousel and image changes
  const observer = new MutationObserver((mutations) => {
    let shouldAdjust = false;
    
    mutations.forEach((mutation) => {
      // Check for active class changes on carousel items
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        const target = mutation.target;
        
        // Check if this is a carousel slide becoming active
        if (target.classList.contains('product-media-item')) {
          if (target.classList.contains('is-active')) {
            shouldAdjust = true;
            console.log('Carousel slide changed - recalculating highlights');
          }
        }
      }
      
      // Check for any image src changes
      if (mutation.type === 'attributes' && mutation.attributeName === 'src') {
        shouldAdjust = true;
      }
    });
    
if (shouldAdjust && window.innerWidth < 1024) {  // Changed from 1024
      // Delay to ensure new image is fully rendered
      setTimeout(() => {
        const productInfo = document.querySelector('product-info');
        if (productInfo && productInfo.adjustMobileHighlights) {
          productInfo.adjustMobileHighlights();
        }
      }, 100);
    }
  });

  // Observe carousel for active slide changes
  const carousel = document.querySelector('#Slider-Gallery-' + this.dataset.section);
  if (carousel) {
    observer.observe(carousel, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'src']
    });
  }

  // Observe both gallery and image elements
  const galleryElement = document.querySelector('.media-gallery-main');
  if (galleryElement) {
    observer.observe(galleryElement, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'style', 'src']
    });
  }
});

// Adjust the thumbnail slider width to span the combined width
document.addEventListener('DOMContentLoaded', function() {
  function adjustThumbnailSliderWidth() {
    // Only run on desktop
    if (window.innerWidth < 990) return;
    
    const mediaWrapper = document.querySelector('.media-and-highlights-wrapper');
    const mediaGallery = document.querySelector('.media-gallery-main');
    const highlightsDesktop = document.querySelector('.product-highlights-desktop');
    const thumbnailSlider = document.querySelector('.thumbnail-slider.grid-item.bottom');
    
    if (!mediaGallery || !thumbnailSlider || !mediaWrapper) return;
    
    // Method 1: Use the wrapper's actual width
    const wrapperRect = mediaWrapper.getBoundingClientRect();
    const wrapperWidth = Math.round(wrapperRect.width);
    
    // Method 2: Calculate based on actual visible elements
    const galleryRect = mediaGallery.getBoundingClientRect();
    let totalWidth = galleryRect.width;
    
    if (highlightsDesktop && window.getComputedStyle(highlightsDesktop).display !== 'none') {
      const highlightsRect = highlightsDesktop.getBoundingClientRect();
      
      // Calculate the rightmost edge
      const rightmostEdge = Math.max(
        galleryRect.right,
        highlightsRect.right
      );
      
      // Calculate the leftmost edge
      const leftmostEdge = Math.min(
        galleryRect.left,
        highlightsRect.left
      );
      
      // Total width is the distance between edges
      totalWidth = rightmostEdge - leftmostEdge;
    }
    
    // Use the smaller of the two calculations to avoid overextending
    const finalWidth = Math.min(wrapperWidth, totalWidth);
    
    // Apply the width to the thumbnail slider
    thumbnailSlider.style.width = `${finalWidth}px`;
    thumbnailSlider.style.maxWidth = `${finalWidth}px`;
    thumbnailSlider.style.marginLeft = '0';
    thumbnailSlider.style.marginRight = '0';
    
    // Make sure the slider is properly aligned
    thumbnailSlider.style.position = 'relative';
    thumbnailSlider.style.left = '0';
    
    // Center the thumbnails within the available space
    const thumbnailList = thumbnailSlider.querySelector('.thumbnail-list');
    if (thumbnailList) {
      thumbnailList.style.justifyContent = 'flex-start';
      thumbnailList.style.width = '100%';
      thumbnailList.style.maxWidth = '100%';
      thumbnailList.style.padding = '0 40px'; // Account for nav buttons
    }
    
    // Log for debugging (only in development)
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('preview')) {
      console.log('Thumbnail slider width adjusted:', {
        wrapperWidth,
        calculatedWidth: totalWidth,
        finalWidth,
        galleryWidth: galleryRect.width,
        highlightsVisible: highlightsDesktop && window.getComputedStyle(highlightsDesktop).display !== 'none'
      });
    }
  }
  
  // Run on load with a slight delay to ensure layout is complete
  setTimeout(adjustThumbnailSliderWidth, 100);
  
  // Run on resize with debouncing
  let resizeTimer;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(adjustThumbnailSliderWidth, 250);
  });
  
  // Re-run when images load
  window.addEventListener('load', function() {
    setTimeout(adjustThumbnailSliderWidth, 200);
  });
  
  // Re-run when variant changes
  document.addEventListener('variant:change', adjustThumbnailSliderWidth);
});

// Enhanced mobile layout JavaScript with Pagination System
document.addEventListener('DOMContentLoaded', function() {
  
  // Mobile detection
  const isMobile = () => window.innerWidth < 768;  // Don't use pagination on tablets
  const isTablet = () => window.innerWidth >= 768 && window.innerWidth < 1025;
  const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // ============================================
  // MOBILE PAGINATION SYSTEM
  // ============================================
  
  class MobilePaginationController {
    constructor() {
      this.currentSlide = 0;
      this.totalSlides = 0;
      this.galleryElement = null;
      this.paginationContainer = null;
      this.dots = [];
      this.isInitialized = false;
      
      this.init();
    }
    
    init() {
      this.findElements();
      this.createPagination();
      this.bindEvents();
      this.updateActiveStates();
      this.isInitialized = true;
      
      if (window.location.hostname === 'localhost') {
        console.log('Mobile pagination initialized:', {
          totalSlides: this.totalSlides,
          currentSlide: this.currentSlide
        });
      }
    }
    
    findElements() {
      this.galleryElement = document.querySelector('#Slider-Gallery-' + this.getSectionId());
      
      if (this.galleryElement) {
        const mediaItems = this.galleryElement.querySelectorAll('.product-media-item');
        this.totalSlides = mediaItems.length;
        
        // Find currently active slide
        const activeItem = this.galleryElement.querySelector('.product-media-item.is-active');
        if (activeItem) {
          const allItems = Array.from(mediaItems);
          this.currentSlide = allItems.indexOf(activeItem);
        }
      }
    }
    
    getSectionId() {
      const productInfo = document.querySelector('product-info');
      return productInfo ? productInfo.dataset.section : 'main-product';
    }
    
    createPagination() {
      if (!isMobile() || this.totalSlides <= 1) {
        this.hidePagination();
        return;
      }
      
      // Find or create pagination container
      let container = document.querySelector('.mobile-pagination-container');
      if (!container) {
        container = document.createElement('div');
        container.className = 'mobile-pagination-container';
        
        // Insert after mobile highlights
        const highlightsMobile = document.querySelector('.product-highlights-mobile');
        if (highlightsMobile) {
          highlightsMobile.parentNode.insertBefore(container, highlightsMobile.nextSibling);
        } else {
          // Fallback: insert after gallery
          const gallery = document.querySelector('.product-media-main');
          if (gallery) {
            gallery.parentNode.insertBefore(container, gallery.nextSibling);
          }
        }
      }
      
      this.paginationContainer = container;
      
      // Create pagination wrapper
      let pagination = container.querySelector('.carousel-pagination');
      if (!pagination) {
        pagination = document.createElement('div');
        pagination.className = 'carousel-pagination';
        pagination.setAttribute('data-gallery-id', this.getSectionId());
        container.appendChild(pagination);
      }
      
      // Clear existing dots
      pagination.innerHTML = '';
      this.dots = [];
      
      // Create dots
      for (let i = 0; i < this.totalSlides; i++) {
        const dot = document.createElement('button');
        dot.className = `pagination-dot${i === this.currentSlide ? ' active' : ''}`;
        dot.setAttribute('data-slide-index', i);
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.setAttribute('type', 'button');
        
        // Add click handler
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          this.goToSlide(i);
        });
        
        pagination.appendChild(dot);
        this.dots.push(dot);
      }
      
      // Show pagination
      container.style.display = 'flex';
    }
    
    bindEvents() {
      if (!this.galleryElement) return;
      
      // Listen for slider changes using mutation observer
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target;
            if (target.classList.contains('product-media-item')) {
              if (target.classList.contains('is-active')) {
                const allItems = Array.from(this.galleryElement.querySelectorAll('.product-media-item'));
                const newIndex = allItems.indexOf(target);
                if (newIndex !== this.currentSlide) {
                  this.currentSlide = newIndex;
                  this.updateActiveStates();
                }
              }
            }
          }
        });
      });
      
      observer.observe(this.galleryElement, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['class']
      });
      
      // Listen for slider button clicks
      const sliderButtons = document.querySelectorAll('.slider-button');
      sliderButtons.forEach(button => {
        button.addEventListener('click', () => {
          setTimeout(() => this.syncWithGallery(), 100);
        });
      });
      
      // Listen for swipe/touch events on gallery
      let startX = 0;
      let startY = 0;
      let currentX = 0;
      let isDragging = false;
      
      if (this.galleryElement && isTouchDevice()) {
        this.galleryElement.addEventListener('touchstart', (e) => {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          isDragging = true;
        });
        
        this.galleryElement.addEventListener('touchmove', (e) => {
          if (!isDragging) return;
          currentX = e.touches[0].clientX;
          const currentY = e.touches[0].clientY;
          
          // Prevent vertical scroll interference
          const deltaX = Math.abs(currentX - startX);
          const deltaY = Math.abs(currentY - startY);
          
          if (deltaX > deltaY && deltaX > 20) {
            e.preventDefault();
          }
        });
        
        this.galleryElement.addEventListener('touchend', () => {
          if (!isDragging) return;
          isDragging = false;
          
          const deltaX = currentX - startX;
          const threshold = 50;
          
          if (Math.abs(deltaX) > threshold) {
            if (deltaX > 0 && this.currentSlide > 0) {
              this.goToSlide(this.currentSlide - 1);
            } else if (deltaX < 0 && this.currentSlide < this.totalSlides - 1) {
              this.goToSlide(this.currentSlide + 1);
            }
          }
          
          setTimeout(() => this.syncWithGallery(), 100);
        });
      }
    }
    
    goToSlide(index) {
      if (index < 0 || index >= this.totalSlides || index === this.currentSlide) return;
      
      const mediaItems = this.galleryElement.querySelectorAll('.product-media-item');
      const targetItem = mediaItems[index];
      
      if (!targetItem) return;
      
      // Method 1: Try clicking the corresponding thumbnail
      const thumbnailId = targetItem.id.replace('Slide-', 'Slide-Thumbnails-');
      const thumbnail = document.querySelector(`#${thumbnailId} button`);
      
      if (thumbnail) {
        thumbnail.click();
      } else {
        // Method 2: Direct manipulation
        this.directSlideChange(index);
      }
      
      // Update states
      this.currentSlide = index;
      this.updateActiveStates();
      
      // Scroll the gallery if it's a horizontal slider
      this.scrollToSlide(index);
    }
    
    directSlideChange(index) {
      const mediaItems = this.galleryElement.querySelectorAll('.product-media-item');
      
      // Remove active class from all items
      mediaItems.forEach(item => item.classList.remove('is-active'));
      
      // Add active class to target item
      if (mediaItems[index]) {
        mediaItems[index].classList.add('is-active');
      }
    }
    
    scrollToSlide(index) {
      const gallery = this.galleryElement;
      if (!gallery || !gallery.scrollTo) return;
      
      const mediaItems = gallery.querySelectorAll('.product-media-item');
      const targetItem = mediaItems[index];
      
      if (targetItem) {
        const itemWidth = targetItem.offsetWidth;
        const scrollPosition = itemWidth * index;
        
        gallery.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
    
    syncWithGallery() {
      if (!this.galleryElement) return;
      
      const activeItem = this.galleryElement.querySelector('.product-media-item.is-active');
      if (activeItem) {
        const allItems = Array.from(this.galleryElement.querySelectorAll('.product-media-item'));
        const newIndex = allItems.indexOf(activeItem);
        
        if (newIndex !== -1 && newIndex !== this.currentSlide) {
          this.currentSlide = newIndex;
          this.updateActiveStates();
        }
      }
    }
    
    updateActiveStates() {
      // Update dot states
      this.dots.forEach((dot, index) => {
        if (index === this.currentSlide) {
          dot.classList.add('active');
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.classList.remove('active');
          dot.removeAttribute('aria-current');
        }
      });
      
      // Update ARIA live region for screen readers
      this.announceSlideChange();
    }
    
    announceSlideChange() {
      let announcer = document.querySelector('.slide-announcer');
      if (!announcer) {
        announcer = document.createElement('div');
        announcer.className = 'slide-announcer visually-hidden';
        announcer.setAttribute('aria-live', 'polite');
        announcer.setAttribute('aria-atomic', 'true');
        document.body.appendChild(announcer);
      }
      
      announcer.textContent = `Slide ${this.currentSlide + 1} of ${this.totalSlides}`;
    }
    
    hidePagination() {
      if (this.paginationContainer) {
        this.paginationContainer.style.display = 'none';
      }
    }
    
    showPagination() {
      if (this.paginationContainer && isMobile() && this.totalSlides > 1) {
        this.paginationContainer.style.display = 'flex';
      }
    }
    
    handleResize() {
      if (isMobile() && this.totalSlides > 1) {
        this.showPagination();
      } else {
        this.hidePagination();
      }
    }
    
    destroy() {
      if (this.paginationContainer) {
        this.paginationContainer.remove();
      }
      this.isInitialized = false;
    }
  }
  
  // Initialize pagination controller
  let paginationController = null;
  
  function initPagination() {
    if (paginationController) {
      paginationController.destroy();
    }
    paginationController = new MobilePaginationController();
  }
  
  // Initialize all mobile optimizations
  function initMobileLayout() {
    // Initialize pagination after layout is settled - only for actual mobile
    if (window.innerWidth < 768) {  // Not for tablets
      setTimeout(initPagination, 100);
    }
  }
  
  // Run on load
  initMobileLayout();
  
  // Handle resize events
  const debouncedResize = (() => {
    let timer;
    return () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (paginationController) {
          paginationController.handleResize();
        }
      }, 250);
    };
  })();
  
  window.addEventListener('resize', debouncedResize);
  
  // Handle variant changes
  document.addEventListener('variant:change', () => {
    setTimeout(() => {
      if (paginationController) {
        paginationController.destroy();
      }
      if (isMobile()) {
        setTimeout(initPagination, 200);
      }
    }, 100);
  });
  
  // Expose pagination controller for external access
  window.MobilePaginationController = paginationController;
  
  // Global function to reinitialize pagination
  window.reinitializePagination = function() {
    if (paginationController) {
      paginationController.destroy();
    }
    if (isMobile()) {
      setTimeout(initPagination, 100);
    }
  };
  
  if (window.location.hostname === 'localhost') {
    console.log('Enhanced mobile layout with pagination system initialized');
  }
});

// Force recalculation on window load and resize
window.addEventListener('DOMContentLoaded', function() {
  const forceHighlightAdjustment = () => {
    const productInfo = document.querySelector('product-info');
    if (productInfo && productInfo.adjustHighlightsHeight) {
      productInfo.adjustHighlightsHeight();
    }
  };
  
  // Initial adjustment
  forceHighlightAdjustment();
  
  // Adjustment after all images load
  window.addEventListener('load', forceHighlightAdjustment);
  
  // Handle dynamic content changes
  const mediaObserver = new MutationObserver(function(mutations) {
    let shouldAdjust = false;
    mutations.forEach(function(mutation) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        if (mutation.target.classList.contains('is-active')) {
          shouldAdjust = true;
        }
      }
    });
    if (shouldAdjust) {
      setTimeout(forceHighlightAdjustment, 100);
    }
  });
  
  const galleryElement = document.querySelector('.media-gallery-main');
  if (galleryElement) {
    mediaObserver.observe(galleryElement, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class']
    });
  }
});

// Ensure mobile alignment on orientation change
window.addEventListener('orientationchange', function() {
  setTimeout(() => {
    const productInfo = document.querySelector('product-info');
    if (productInfo && productInfo.adjustHighlightsHeight) {
      productInfo.adjustHighlightsHeight();
    }
  }, 300);
});

// Re-align on image load for mobile
document.addEventListener('DOMContentLoaded', function() {
  const images = document.querySelectorAll('.product__media img');
  images.forEach(img => {
    if (!img.complete) {
      img.addEventListener('load', function() {
        const productInfo = document.querySelector('product-info');
        if (productInfo && window.innerWidth < 1024 ) {
          productInfo.adjustMobileHighlights();
        }
      });
    }
  });
});

